// AddTimesheetModal.jsx

import React, { useEffect, useState } from 'react';
import {
  Modal,
  Form,
  DatePicker,
  TimePicker,
  Button,
  Switch,
  Input,
  Space,
  message,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';

import { baseUrl_SPB } from '../BaseUrl';

const AddTimesheetModal = ({
  open,
  onClose,
  onSave,
  token,
  resource,
  selectedItem,
  isUserManager,
  companyId,
}) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      loadTimesheet();
    }
  }, [selectedItem, open]);

  const loadTimesheet = async () => {
    try {
      if (!selectedItem?.id) {
        form.resetFields();

        form.setFieldsValue({
          date: dayjs(),
          startTime: dayjs(),
          endTime: dayjs(),
          includeWeekends: false,
          allowances: [
            {
              allowanceType: '',
              unit: '',
              value: 0,
            },
          ],
        });

        return;
      }

      const response = await axios.get(
        `${baseUrl_SPB}/api/v1/rostertimeallocation/fetchtimesheetbyid?id=${selectedItem.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const ts = response.data;

      form.setFieldsValue({
        date: dayjs(ts.date, 'DD-MM-YYYY'),
        startTime: dayjs(ts.starttime, 'HH:mm'),
        endTime: dayjs(ts.endtime, 'HH:mm'),
        includeWeekends: ts.includeWeekends,
        allowances:
          ts.timeAllocationAllowances ||
          [],
      });
    } catch (err) {
      console.error(err);
      message.error(
        'Failed to load timesheet'
      );
    }
  };

  const handleSave = async () => {
    try {
      const values =
        await form.validateFields();

      setSaving(true);

      const payload = {
        id: selectedItem?.id,
        ...selectedItem,
        status: 'timesheet',
        approvalStatus:
          selectedItem?.approvalStatus ||
          'pending',

        staffId: resource.id,
        companyId,

        date:
          values.date.format(
            'DD-MM-YYYY'
          ),

        starttime:
          values.startTime.format(
            'HH:mm'
          ),

        endtime:
          values.endTime.format(
            'HH:mm'
          ),

        includeWeekends:
          values.includeWeekends,

        timeAllocationAllowances:
          values.allowances || [],
      };

      await axios.post(
        `${baseUrl_SPB}/api/v1/rostertimeallocation/savelist`,
        [payload],
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      message.success(
        selectedItem?.id
          ? 'Timesheet updated'
          : 'Timesheet added'
      );

      onSave?.();
      onClose?.();
    } catch (err) {
      if (err.errorFields) return;

      console.error(err);
      message.error(
        'Failed to save timesheet'
      );
    } finally {
      setSaving(false);
    }
  };

  const canEdit =
    isUserManager ||
    !selectedItem?.id ||
    !selectedItem?.approvalStatus;

  return (
    <Modal
      open={open}
      title={
        selectedItem?.id
          ? 'Edit Timesheet'
          : 'Add Timesheet'
      }
      onCancel={onClose}
      onOk={handleSave}
      confirmLoading={saving}
      width={700}
      okButtonProps={{
        disabled: !canEdit,
      }}
    >
      {!canEdit && (
        <div
          style={{
            marginBottom: 16,
            color: '#fa8c16',
          }}
        >
          This timesheet has already
          been approved.
        </div>
      )}

      <Form
        form={form}
        layout="vertical"
      >
        <Form.Item
          label="Date"
          name="date"
          rules={[
            {
              required: true,
              message:
                'Please select date',
            },
          ]}
        >
          <DatePicker
            style={{ width: '100%' }}
            format="DD-MM-YYYY"
          />
        </Form.Item>

        <Form.Item
          label="Start Time"
          name="startTime"
          rules={[
            {
              required: true,
            },
          ]}
        >
          <TimePicker
            format="HH:mm"
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item
          label="End Time"
          name="endTime"
          rules={[
            {
              required: true,
            },
          ]}
        >
          <TimePicker
            format="HH:mm"
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item
          label="Include Weekends"
          name="includeWeekends"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Divider>
          Allowances
        </Divider>

        <Form.List name="allowances">
          {(
            fields,
            {
              add,
              remove,
            }
          ) => (
            <>
              {fields.map(
                ({
                  key,
                  name,
                }) => (
                  <Space
                    key={key}
                    style={{
                      display:
                        'flex',
                      marginBottom:
                        8,
                    }}
                    align="baseline"
                  >
                    <Form.Item
                      name={[
                        name,
                        'allowanceType',
                      ]}
                    >
                      <Input placeholder="Allowance Type" />
                    </Form.Item>

                    <Form.Item
                      name={[
                        name,
                        'unit',
                      ]}
                    >
                      <Input placeholder="Unit" />
                    </Form.Item>

                    <Form.Item
                      name={[
                        name,
                        'value',
                      ]}
                    >
                      <Input
                        type="number"
                        placeholder="Value"
                      />
                    </Form.Item>

                    <Button
                      danger
                      icon={
                        <DeleteOutlined />
                      }
                      onClick={() =>
                        remove(
                          name
                        )
                      }
                    />
                  </Space>
                )
              )}

              <Button
                type="dashed"
                block
                icon={
                  <PlusOutlined />
                }
                onClick={() =>
                  add({
                    allowanceType:
                      '',
                    unit: '',
                    value: 0,
                  })
                }
              >
                Add Allowance
              </Button>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};

export default AddTimesheetModal;